/**
 * Alshallal Pastry Company - Company Settings Page for Letters
 * Phase 25: Letters & Templates System
 * 
 * Features:
 * - Manage company letterhead information
 * - Set default signatory
 * - Upload company logo
 * - Access: HR Admin, Super Admin only
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Building2,
  Save,
  Loader2,
  Phone,
  Mail,
  Globe,
  FileText,
  User,
  MapPin,
  Hash
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CompanySettingsPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const [formData, setFormData] = useState({
    company_name_ar: '',
    company_name_en: '',
    cr_number: '',
    address_ar: '',
    address_en: '',
    city_ar: '',
    city_en: '',
    phone: '',
    email: '',
    website: '',
    default_signatory_name_ar: '',
    default_signatory_name_en: '',
    default_signatory_title_ar: '',
    default_signatory_title_en: '',
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/letters/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setFormData({
          company_name_ar: data.company_name_ar || '',
          company_name_en: data.company_name_en || '',
          cr_number: data.cr_number || '',
          address_ar: data.address_ar || '',
          address_en: data.address_en || '',
          city_ar: data.city_ar || '',
          city_en: data.city_en || '',
          phone: data.phone || '',
          email: data.email || '',
          website: data.website || '',
          default_signatory_name_ar: data.default_signatory_name_ar || '',
          default_signatory_name_en: data.default_signatory_name_en || '',
          default_signatory_title_ar: data.default_signatory_title_ar || '',
          default_signatory_title_en: data.default_signatory_title_en || '',
        });
        setIsNew(false);
      } else if (res.status === 404) {
        setIsNew(true);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error(isRTL ? 'خطأ في تحميل الإعدادات' : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  }, [token, isRTL]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.company_name_ar || !formData.company_name_en) {
      toast.error(isRTL ? 'اسم الشركة مطلوب' : 'Company name is required');
      return;
    }
    if (!formData.cr_number) {
      toast.error(isRTL ? 'رقم السجل التجاري مطلوب' : 'CR number is required');
      return;
    }

    setSaving(true);
    try {
      const url = isNew 
        ? `${API}/api/letters/admin/settings/initialize`
        : `${API}/api/letters/admin/settings`;
      
      const method = isNew ? 'POST' : 'PATCH';

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setIsNew(false);
        toast.success(isRTL ? 'تم حفظ الإعدادات' : 'Settings saved successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail?.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(isRTL ? 'خطأ في الحفظ' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-6 max-w-4xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            {isRTL ? 'إعدادات الشركة للخطابات' : 'Company Settings for Letters'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isRTL ? 'إعداد بيانات الشركة للترويسة والخطابات الرسمية' : 'Configure company information for letterhead and official letters'}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {isRTL ? 'حفظ' : 'Save'}
        </Button>
      </div>

      {isNew && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <p className="text-yellow-800">
            {isRTL 
              ? 'لم يتم إعداد بيانات الشركة بعد. يرجى ملء المعلومات أدناه لتفعيل نظام الخطابات.'
              : 'Company settings have not been configured yet. Please fill in the information below to activate the letters system.'}
          </p>
        </div>
      )}

      {/* Company Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {isRTL ? 'هوية الشركة' : 'Company Identity'}
          </CardTitle>
          <CardDescription>
            {isRTL ? 'المعلومات الأساسية للشركة' : 'Basic company information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم الشركة (عربي)' : 'Company Name (Arabic)'}</Label>
              <Input
                value={formData.company_name_ar}
                onChange={(e) => handleChange('company_name_ar', e.target.value)}
                placeholder="شركة ..."
                dir="rtl"
                data-testid="company-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم الشركة (إنجليزي)' : 'Company Name (English)'}</Label>
              <Input
                value={formData.company_name_en}
                onChange={(e) => handleChange('company_name_en', e.target.value)}
                placeholder="Company Name..."
                data-testid="company-name-en"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              {isRTL ? 'رقم السجل التجاري' : 'Commercial Registration (CR) Number'}
            </Label>
            <Input
              value={formData.cr_number}
              onChange={(e) => handleChange('cr_number', e.target.value)}
              placeholder="1010XXXXXX"
              data-testid="cr-number"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {isRTL ? 'معلومات الاتصال' : 'Contact Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'العنوان (عربي)' : 'Address (Arabic)'}</Label>
              <Input
                value={formData.address_ar}
                onChange={(e) => handleChange('address_ar', e.target.value)}
                placeholder="..."
                dir="rtl"
                data-testid="address-ar"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'العنوان (إنجليزي)' : 'Address (English)'}</Label>
              <Input
                value={formData.address_en}
                onChange={(e) => handleChange('address_en', e.target.value)}
                placeholder="..."
                data-testid="address-en"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'المدينة (عربي)' : 'City (Arabic)'}</Label>
              <Input
                value={formData.city_ar}
                onChange={(e) => handleChange('city_ar', e.target.value)}
                placeholder="الرياض"
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'المدينة (إنجليزي)' : 'City (English)'}</Label>
              <Input
                value={formData.city_en}
                onChange={(e) => handleChange('city_en', e.target.value)}
                placeholder="Riyadh"
              />
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {isRTL ? 'الهاتف' : 'Phone'}
              </Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+966 11 XXX XXXX"
                data-testid="phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {isRTL ? 'البريد الإلكتروني' : 'Email'}
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@company.com"
                data-testid="email"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {isRTL ? 'الموقع الإلكتروني' : 'Website'}
              </Label>
              <Input
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://company.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Signatory */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {isRTL ? 'المخول بالتوقيع' : 'Default Signatory'}
          </CardTitle>
          <CardDescription>
            {isRTL ? 'الشخص المخول بتوقيع الخطابات الرسمية' : 'The person authorized to sign official letters'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم المخول (عربي)' : 'Signatory Name (Arabic)'}</Label>
              <Input
                value={formData.default_signatory_name_ar}
                onChange={(e) => handleChange('default_signatory_name_ar', e.target.value)}
                placeholder="..."
                dir="rtl"
                data-testid="signatory-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم المخول (إنجليزي)' : 'Signatory Name (English)'}</Label>
              <Input
                value={formData.default_signatory_name_en}
                onChange={(e) => handleChange('default_signatory_name_en', e.target.value)}
                placeholder="..."
                data-testid="signatory-name-en"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'المسمى الوظيفي (عربي)' : 'Title (Arabic)'}</Label>
              <Input
                value={formData.default_signatory_title_ar}
                onChange={(e) => handleChange('default_signatory_title_ar', e.target.value)}
                placeholder="مدير الموارد البشرية"
                dir="rtl"
                data-testid="signatory-title-ar"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'المسمى الوظيفي (إنجليزي)' : 'Title (English)'}</Label>
              <Input
                value={formData.default_signatory_title_en}
                onChange={(e) => handleChange('default_signatory_title_en', e.target.value)}
                placeholder="HR Manager"
                data-testid="signatory-title-en"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                {isRTL ? 'ملاحظة' : 'Note'}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {isRTL 
                  ? 'هذه المعلومات ستظهر في ترويسة جميع الخطابات الرسمية الصادرة من النظام.'
                  : 'This information will appear in the letterhead of all official letters issued from the system.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
